import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Icon from '../Icon';
import { useVariants } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
export const tokenTypes = {
  itemIconSize: PropTypes.number.isRequired,
  itemIconColor: PropTypes.string.isRequired,
  listGutter: PropTypes.number.isRequired,
  iconMarginTop: PropTypes.number.isRequired
};
const selectItemIconTokens = _ref => {
  let {
    itemIconSize,
    itemIconColor
  } = _ref;
  return {
    size: itemIconSize,
    color: itemIconColor
  };
};
const selectSideItemContainerStyles = _ref2 => {
  let {
    listGutter,
    iconMarginTop
  } = _ref2;
  return {
    marginTop: iconMarginTop,
    marginRight: listGutter
  };
};

// Align bullets with the top line of text the same way icons are aligned
const selectBulletPositioningStyles = _ref3 => {
  let {
    itemIconSize
  } = _ref3;
  return {
    width: itemIconSize,
    height: itemIconSize
  };
};
const selectBulletContainerStyles = _ref4 => {
  let {
    itemBulletContainerWidth,
    itemBulletContainerHeight,
    itemBulletContainerAlign
  } = _ref4;
  return {
    width: itemBulletContainerWidth,
    height: itemBulletContainerHeight,
    alignItems: itemBulletContainerAlign
  };
};
const getIconColorVariants = iconVariants => iconVariants?.filter(variant => variant[0] === 'color').map(variant => variant[1]);

/**
 * Subcomponent used within ListItem and similar components for rendering bullets or icons
 * that sit alongside a ListIconContent in a { flexDirection: row } container.
 *
 * It's the responsibility of themes to make sure that the supplied tokens align the
 * icon or bullet nicely against the first line of text in a ListIconContent.
 */
const ListItemMark = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  let {
    icon,
    iconColor,
    iconSize,
    tokens = {}
  } = _ref5;
  const themeTokens = typeof tokens === 'function' ? tokens() : tokens;
  const sideItemContainerStyles = selectSideItemContainerStyles(themeTokens);
  const bulletContainerStyles = selectBulletContainerStyles(themeTokens);

  // TODO: Remove it when iconColor custom colors are deprecated.
  const iconVariants = useVariants('Icon');
  const iconColorVariants = getIconColorVariants(iconVariants);
  if (icon) {
    const iconTokens = selectItemIconTokens(themeTokens);
    return /*#__PURE__*/_jsx(View, {
      style: [sideItemContainerStyles, bulletContainerStyles],
      children: /*#__PURE__*/_jsx(Icon, {
        icon: icon,
        tokens: {
          size: iconSize ?? iconTokens.size,
          ...((iconColor && !iconColorVariants?.includes(iconColor) || !iconColor) && {
            color: iconColor && !iconColorVariants?.includes(iconColor) ? iconColor : iconTokens.color
          })
        },
        variant: {
          ...(iconColorVariants?.includes(iconColor) && {
            color: iconColor
          })
        }
      })
    });
  }
  const bulletColor = themeTokens.itemBulletColor;
  const {
    bulletIcon
  } = themeTokens;
  const itemBulletContainerStyles = selectBulletContainerStyles(themeTokens);
  const itemBulletPositioningStyles = selectBulletPositioningStyles(themeTokens);
  return /*#__PURE__*/_jsx(View, {
    style: [sideItemContainerStyles, itemBulletContainerStyles],
    ref: ref,
    children: /*#__PURE__*/_jsx(View, {
      style: [staticStyles.bulletPositioning, itemBulletPositioningStyles],
      testID: "unordered-item-bullet",
      children: /*#__PURE__*/_jsx(Icon, {
        icon: bulletIcon,
        tokens: {
          color: bulletColor,
          size: themeTokens.itemIconSize
        }
      })
    })
  });
});
ListItemMark.displayName = 'ListItemMark';
ListItemMark.propTypes = {
  tokens: PropTypes.shape(tokenTypes).isRequired,
  /**
   * Renders side item icon
   */
  icon: PropTypes.elementType,
  /**
   * Will set display icon color
   */
  iconColor: PropTypes.string,
  /**
   * Allow the user define the icon size
   */
  iconSize: PropTypes.number
};
const staticStyles = StyleSheet.create({
  bulletPositioning: {
    alignItems: 'center',
    justifyContent: 'center'
  }
});
export default ListItemMark;